/**
 * CR500 Phase 2B: Details Presentation Wrapper
 * 
 * Unified wrapper that renders deal details in the configured presentation mode.
 * Supports flip (current), modal, sidePanel, and inlineExpand modes.
 * All modes render the same content from surfaces.details.blocks.
 */

import React, { useState, useCallback } from "react";
import type { Deal } from "@/lib/types/deals";
import type { 
  ResolvedDealCardSpecV2, 
  DealCardSurfaceKey,
} from "@/lib/deal-cards/specV2/types";
import type { 
  DetailsPresentationConfig, 
  DetailsPresentationMode,
} from "@/lib/layout-studio/detailsPresentationTypes";
import { DEFAULT_DETAILS_PRESENTATION } from "@/lib/layout-studio/detailsPresentationTypes";

// Import presentation mode components
import { FlipPresentation } from "./FlipPresentation";
import { ModalPresentation } from "./ModalPresentation";
import { SidePanelPresentation } from "./SidePanelPresentation";
import { InlineExpandPresentation } from "./InlineExpandPresentation";

// ================================================================
// TYPES
// ================================================================

export interface DetailsPresentationWrapperProps {
  /** The deal being displayed */
  deal: Deal & { id?: string; dedupe_key?: string };
  /** The resolved v2 spec (for rendering details blocks) */
  spec: ResolvedDealCardSpecV2;
  /** Presentation configuration from layout preset */
  presentation?: DetailsPresentationConfig;
  /** Admin context flag */
  isAdmin?: boolean;
  /** The primary card content (children) */
  children: React.ReactNode;
  /** Unique identifier for this deal in the list (for expand tracking) */
  dealId?: string;
  /** External expanded state (for inlineExpand mode) */
  isExpanded?: boolean;
  /** External expand toggle handler */
  onExpandToggle?: () => void;
  /** Height class for flip mode */
  heightClass?: string;
  /** Height in pixels for flip mode */
  heightPx?: number;
  /** Debug mode */
  debug?: boolean;
}

// ================================================================
// COMPONENT
// ================================================================

export function DetailsPresentationWrapper({
  deal,
  spec,
  presentation = DEFAULT_DETAILS_PRESENTATION,
  isAdmin = false,
  children,
  dealId,
  isExpanded = false,
  onExpandToggle,
  heightClass = "h-[400px]",
  heightPx,
  debug = false,
}: DetailsPresentationWrapperProps) {
  // Internal state for modal/panel open
  const [isOpen, setIsOpen] = useState(false);
  
  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);
  
  // Debug logging
  if (debug && isAdmin) {
    console.debug("[DetailsPresentationWrapper]", deal.id || deal.dedupe_key, {
      mode: presentation.mode,
      entry: presentation.entry,
    });
  }
  
  // Render based on presentation mode
  switch (presentation.mode) {
    case "flip":
      return (
        <FlipPresentation
          deal={deal}
          spec={spec}
          isAdmin={isAdmin}
          heightClass={heightClass}
          heightPx={heightPx}
          entryConfig={presentation.entry}
        >
          {children}
        </FlipPresentation>
      );
    
    case "modal":
      return (
        <ModalPresentation
          deal={deal}
          spec={spec}
          isAdmin={isAdmin}
          isOpen={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          config={presentation.modal}
          entryConfig={presentation.entry}
        >
          {children}
        </ModalPresentation>
      );
    
    case "sidePanel":
      return (
        <SidePanelPresentation
          deal={deal}
          spec={spec}
          isAdmin={isAdmin}
          isOpen={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          config={presentation.sidePanel}
          entryConfig={presentation.entry}
        >
          {children}
        </SidePanelPresentation>
      );
    
    case "inlineExpand":
      return (
        <InlineExpandPresentation
          deal={deal}
          spec={spec}
          isAdmin={isAdmin}
          isExpanded={isExpanded}
          onToggle={onExpandToggle}
          config={presentation.inlineExpand}
          entryConfig={presentation.entry}
        >
          {children}
        </InlineExpandPresentation>
      );
    
    default:
      // Fallback to flip
      return (
        <FlipPresentation
          deal={deal}
          spec={spec}
          isAdmin={isAdmin}
          heightClass={heightClass}
          heightPx={heightPx}
          entryConfig={presentation.entry}
        >
          {children}
        </FlipPresentation>
      );
  }
}

export default DetailsPresentationWrapper;
